import { useEffect, useRef } from "react";
import {
  Radio,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Bell,
  BellRing,
  Settings2,
  Building2,
  CloudSun,
  Network,
  LineChart,
  Users,
  UserCircle,
  Briefcase,
  Zap,
  Brain,
  X,
  Cable,
} from "lucide-react";
import { Logo } from "../../components/shared/ui/Logo";
import { cn } from "@/lib/utils";
import { NavLink } from "react-router-dom";
import { ThemeToggle } from "../shared/ThemeToggle";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { usePermissions } from "@/utils/permissions";
import { FEATURES } from "@/config/featureFlags";
import { prefetchRoute } from "@/lib/routeLoaders";
import type { LucideIcon } from "lucide-react";

interface SidebarProps {
  isExpanded: boolean;
  isMobileOpen: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  onMobileOpenChange: (isOpen: boolean) => void;
  hasActiveStations: boolean;
  hasActiveMemberships: boolean;
  isUserActive: boolean;
  permissions: ReturnType<typeof usePermissions>;
  onSignOut: () => void;
  onNotificationsToggle: () => void;
  /** Intención de abrir el panel (hover/foco): permite precargar su chunk. */
  onNotificationsIntent?: () => void;
  showNotifications: boolean;
}

interface NavItem {
  view: string;
  path: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  condition?: boolean;
}

export function Sidebar({
  isExpanded,
  isMobileOpen,
  onExpandedChange,
  onMobileOpenChange,
  hasActiveStations,
  hasActiveMemberships,
  isUserActive,
  permissions,
  onSignOut,
  onNotificationsToggle,
  onNotificationsIntent,
  showNotifications,
}: SidebarProps) {
  const isMobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement>(null);

  // El drawer móvil siempre muestra las etiquetas; sólo el escritorio colapsa.
  const expanded = isMobile ? true : isExpanded;
  // Fuera de pantalla en móvil: el panel sigue montado (para la animación) pero
  // no debe recibir foco ni ser leído. Antes se montaban DOS listas a la vez y
  // los enlaces de la oculta seguían siendo tabulables.
  const isPanelHidden = isMobile && !isMobileOpen;

  // `inert` se pone por referencia porque React 18 no lo tipa como propiedad.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (isPanelHidden) {
      panel.setAttribute("inert", "");
    } else {
      panel.removeAttribute("inert");
    }
  }, [isPanelHidden]);

  // Drawer móvil abierto: el foco queda atrapado dentro y vuelve al botón que
  // lo abrió al cerrarse.
  useFocusTrap(panelRef, isMobile && isMobileOpen);

  // Configuración de elementos de navegación
  const navItems: NavItem[] = [
    {
      view: "overview",
      path: "/",
      label: "Vista General",
      icon: Radio,
      end: true,
      condition: hasActiveMemberships,
    },
    {
      view: "network",
      path: "/network",
      label: "Red",
      icon: Network,
      condition: hasActiveStations && isUserActive && hasActiveMemberships,
    },
    {
      view: "climate",
      path: "/climate",
      label: "Clima",
      icon: CloudSun,
      condition: hasActiveStations && isUserActive && hasActiveMemberships,
    },
    {
      view: "analysis",
      path: "/analysis",
      label: "Análisis",
      icon: LineChart,
      condition: hasActiveStations && isUserActive && hasActiveMemberships,
    },
    {
      view: "alerts",
      path: "/alerts",
      label: "Alertas",
      icon: Bell,
      // La barra inferior y la cabecera móviles ya llevaban a /alerts: sin esta
      // entrada el escritorio ofrecía una navegación distinta.
      condition: hasActiveMemberships,
    },
    {
      view: "crops",
      path: "/crops",
      label: "Cultivos",
      icon: Briefcase,
      // OCULTO (feature futura, ver config/featureFlags.ts). NO es código muerto.
      condition: FEATURES.crops && hasActiveStations && isUserActive && hasActiveMemberships,
    },
    {
      view: "imageAnalysis",
      path: "/image-analysis",
      label: "Análisis de Imágenes",
      icon: Brain,
      // OCULTO (feature futura, ver config/featureFlags.ts). NO es código muerto.
      condition: FEATURES.imageAnalysis && hasActiveStations && isUserActive && hasActiveMemberships,
    },
    {
      view: "registration",
      path: "/registration",
      label: "Registro",
      icon: Building2,
      condition: hasActiveMemberships && permissions.canManageRegistration(),
    },
    {
      view: "actuators",
      path: "/actuators",
      label: "Actuadores",
      icon: Zap,
      condition: hasActiveMemberships && permissions.canManageActuators(),
    },
    {
      view: "rules",
      path: "/rules",
      label: "Reglas de Alerta",
      icon: BellRing,
      condition: hasActiveMemberships && permissions.canManageAlertRules(),
    },
    {
      view: "systemSettings",
      path: "/system-settings",
      label: "Configuraciones",
      icon: Settings2,
      condition: hasActiveMemberships && permissions.canManageSystemSettings(),
    },
    {
      view: "usersManagement",
      path: "/users-management",
      label: "Usuarios",
      icon: Users,
      condition: hasActiveMemberships && permissions.canManageStationMembers(),
    },
    {
      view: "profile",
      path: "/profile",
      label: "Mi Cuenta",
      icon: UserCircle,
    },
    {
      view: "integrations",
      path: "/integrations",
      label: "Integraciones",
      icon: Cable,
      condition: isUserActive,
    },
  ];

  const renderNavLink = (item: NavItem) => {
    if (item.condition === false) return null;
    const Icon = item.icon;

    return (
      <NavLink
        key={item.view}
        to={item.path}
        end={item.end}
        onClick={() => {
          // En móvil, cerrar el drawer tras navegar (el NavLink navega).
          if (isMobile) onMobileOpenChange(false);
        }}
        // Precarga el chunk de la página en cuanto hay intención (puntero,
        // foco de teclado o dedo): al soltar el clic la ruta ya está lista.
        onPointerEnter={() => prefetchRoute(item.path)}
        onFocus={() => prefetchRoute(item.path)}
        onTouchStart={() => prefetchRoute(item.path)}
        className={({ isActive }) =>
          cn(
            "relative flex min-h-11 w-full items-center rounded-lg transition-colors duration-fast ease-smooth",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
            expanded ? "gap-3 px-3 py-2" : "justify-center p-3",
            isActive
              ? "bg-primary/10 font-medium text-primary"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
          )
        }
        title={!expanded ? item.label : undefined}
        aria-label={!expanded ? item.label : undefined}
      >
        {({ isActive }) => (
          <>
            {/* Barra de acento a la izquierda: en modo oscuro el fondo del item
                activo apenas se distingue del resto. */}
            <span
              aria-hidden="true"
              className={cn(
                "absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-primary",
                "transition-[opacity,transform] duration-fast ease-smooth",
                isActive ? "scale-y-100 opacity-100" : "scale-y-0 opacity-0"
              )}
            />
            <Icon
              className={cn(
                "h-5 w-5 shrink-0",
                isActive ? "text-primary" : "text-muted-foreground"
              )}
              aria-hidden="true"
            />
            {expanded && <span className="truncate">{item.label}</span>}
          </>
        )}
      </NavLink>
    );
  };

  const notificationsButton = (
    <button
      type="button"
      onClick={onNotificationsToggle}
      onPointerEnter={onNotificationsIntent}
      onFocus={onNotificationsIntent}
      aria-label="Notificaciones"
      aria-pressed={showNotifications}
      title="Notificaciones"
      className={cn(
        "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors duration-fast ease-smooth sm:h-10 sm:w-10",
        showNotifications
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-accent hover:text-foreground"
      )}
    >
      <Bell className="h-5 w-5 text-warning" />
    </button>
  );

  return (
    <div
      ref={panelRef}
      id="app-sidebar"
      // En móvil es un drawer modal; en escritorio es una columna permanente del
      // shell, así que allí NO debe anunciarse como diálogo.
      role={isMobile ? "dialog" : undefined}
      aria-modal={isMobile ? true : undefined}
      aria-label={isMobile ? "Menú" : undefined}
      aria-hidden={isPanelHidden || undefined}
      className={cn(
        // pt-safe/pb-safe respetan el notch y la barra de gestos cuando el
        // drawer está a pantalla completa en móvil; en desktop env()=0.
        // pl-safe: en horizontal con notch a la izquierda el drawer quedaba
        // parcialmente bajo el recorte de la pantalla.
        "fixed inset-y-0 left-0 z-drawer flex flex-col border-r border-border bg-card pb-safe pl-safe pt-safe",
        "transition-[transform,width] duration-moderate ease-smooth",
        expanded ? "w-64 max-w-[85vw] md:max-w-none" : "w-20",
        isMobileOpen ? "translate-x-0" : "-translate-x-full",
        "md:translate-x-0"
      )}
    >
      {/* Cabecera */}
      <div
        className={cn(
          "flex min-h-16 items-center border-b border-border px-3",
          expanded ? "justify-between gap-2" : "justify-center"
        )}
      >
        {expanded && (
          // Logo compacto + truncate: en w-64 el logo grande con texto xl
          // recortaba el título y comprimía el botón de colapsar.
          <div className="flex min-w-0 items-center gap-2">
            <Logo className="h-10 w-10 shrink-0" />
            <span className="truncate text-base font-semibold text-foreground">
              Agricos Monitor
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={() =>
            isMobile ? onMobileOpenChange(false) : onExpandedChange(!isExpanded)
          }
          aria-label={
            isMobile ? "Cerrar menú" : expanded ? "Colapsar menú" : "Expandir menú"
          }
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors duration-fast ease-smooth hover:bg-accent hover:text-foreground"
        >
          {isMobile ? (
            <X className="h-5 w-5" />
          ) : expanded ? (
            <ChevronLeft className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Un solo landmark de navegación con nombre: antes había dos <nav> sin
          etiqueta (variante móvil y variante escritorio) montados a la vez. */}
      <nav
        aria-label="Navegación principal"
        className={cn(
          "flex-1 space-y-1 overflow-y-auto py-3",
          expanded ? "px-3" : "px-2"
        )}
      >
        {navItems.map(renderNavLink)}
      </nav>

      {/* Pie */}
      <div className={cn("border-t border-border py-3", expanded ? "px-3" : "px-2")}>
        {expanded ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onSignOut}
              className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-lg px-2 text-sm text-muted-foreground transition-colors duration-fast ease-smooth hover:bg-accent hover:text-foreground sm:h-10"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span className="truncate whitespace-nowrap">Cerrar Sesión</span>
            </button>
            <ThemeToggle />
            {notificationsButton}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <button
              type="button"
              onClick={onSignOut}
              aria-label="Cerrar Sesión"
              title="Cerrar Sesión"
              className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors duration-fast ease-smooth hover:bg-accent hover:text-foreground sm:h-10 sm:w-10"
            >
              <LogOut className="h-5 w-5" />
            </button>
            <ThemeToggle />
            {notificationsButton}
          </div>
        )}
      </div>
    </div>
  );
}
